import React from 'react';
import { observer } from 'mobx-react';
import { isSome, renderIf, translate, findUserAction } from 'core/utils';
import moment from 'moment';
import _ from 'lodash';
import { historyLogListFields, historyLogUtcDateTimeFields } from 'common/constants';

function HistoryLogTemplate({ listStore, order }) {
	return (
		<React.Fragment>
			{isSome(listStore.response) &&
			isSome(listStore.response.item) &&
			listStore.response.item.length > 0 &&
			listStore.response.item.some(element => isSome(element.changed) && element.changed.length > 0) ? (
				_.map(listStore.response.item, (item, index) => {
					if (isSome(item.changed) && item.changed.length > 0) {
						return (
							<div data-cy={'viewActivityCard'} key={index} className="activity-log__card spc--bottom--sml">
								<div data-cy={'viewActivityTimestamp'} className="activity-log__card__header">
									<h4>
										{
											moment.utc(item.dateCreated)
												.local()
												.format('MMMM Do YYYY')
												.split(',')[0]
										}
										<span className="type--tny spc--left--tny spc--right--tny"> &#8226; </span>
										{moment.utc(item.dateCreated)
											.local()
											.toDate()
											.toLocaleTimeString()}
									</h4>
									<label className="type--sml type--color--grey-5">{findUserAction(item.userAction)}</label>
								</div>
								<div data-cy={'viewActivityUser'} className="activity-log__seller">
									<span className="fas fa-user-alt type--tny spc--right--tny"></span>
									<span>{item.user != null ? item.user.displayName.split('-')[0] : ''}</span>
								</div>
								<div className="activity-log__list__fake"></div>
								<ul className="activity-log__list">
									{isSome(item.changed) &&
										item.changed.length > 0 &&
										_.map(item.changed, (changedItem, index) => {
											const sentenceRenderer = sentenceLogRenderers[changedItem.name];
											if (sentenceRenderer && changedItem.initialValue === '') {
												return (
													<li key={index} className="activity-log__list__item">
														{sentenceRenderer(changedItem.newValue)}
													</li>
												);
											}

											if (historyLogListFields.includes(changedItem.name)) {
												if (['', '(new)'].includes(changedItem.initialValue)) {
													return (
														<ListFieldLog
															name={changedItem.name}
															index={index}
															action={'Added'}
															value={changedItem.newValue}
														/>
													);
												} else if (['', '(deleted)'].includes(changedItem.newValue)) {
													return (
														<ListFieldLog
															name={changedItem.name}
															index={index}
															action={'Deleted'}
															value={changedItem.initialValue}
														/>
													);
												}
											}

											const initialValue = formatChangeValue(changedItem.name, changedItem.initialValue);
											const newValue = formatChangeValue(changedItem.name, changedItem.newValue);

											return (
												<li key={index} className="activity-log__list__item">
													Changed <span className="type--wgt--bold">{changedItem.name}</span>
													{initialValue === '' ? '' : ' from '}
													{renderIf(initialValue != '')(
														<span className="activity-log__badge activity-log__badge--old">
															{initialValue}
														</span>
													)}{' '}
													to{' '}
													<span className="activity-log__badge activity-log__badge--new">
														{renderNewValue(newValue)}
													</span>
												</li>
											);
										})}
								</ul>
							</div>
						);
					}
				})
			) : isSome(listStore.response) &&
			  isSome(listStore.response.item) &&
			  (listStore.response.item.length == 0 ||
					listStore.response.item.every(
						element => !isSome(element.changed) || (isSome(element.changed) && element.changed.length === 0)
					)) ? (
				<p className="flex--primary flex--align--h--center padd--top--sml">{translate('LISTEMPTY.HISTORYLOG')}</p>
			) : (
				<div className="pos--rel h--60">
					<div className="spinner spinner--g"></div>
				</div>
			)}
		</React.Fragment>
	);
}

// BE writes DateTime change values with DateTime.ToString() (en-US server culture).
const historyLogDateTimeFormat = 'M/D/YYYY h:mm:ss A';

// Returns a display value without mutating the store item, so re-renders never re-convert.
const formatChangeValue = (name, value) => {
	if (!value) {
		return value;
	}

	if (name.includes('WH Ship By Date')) {
		const date = moment(value, historyLogDateTimeFormat, true);
		return date.isValid() ? date.format('L') : value;
	}

	if (historyLogUtcDateTimeFields.includes(name)) {
		const dateTime = moment.utc(value, historyLogDateTimeFormat, true);
		return dateTime.isValid() ? dateTime.local().format('L LT') : value;
	}

	return value;
};

const renderNewValue = value => {
	if (value && (value.toLowerCase().includes('url:') || value.toLowerCase().includes('https'))) {
		return <TextWithUrl value={value} />;
	}
	if (value !== '') {
		return value;
	}
	return 'None';
};

const TextWithUrl = ({ value }) => {
	const urlRegex = /(https?:\/\/[^\s]+)/g;
	const parts = [];
	let lastIndex = 0;

	// Iterate over all URLs in the string
	let match;
	while ((match = urlRegex.exec(value)) !== null) {
		const url = match[0];
		const index = match.index;

		// Add text before the URL (if any)
		if (index > lastIndex) {
			parts.push({ text: value.slice(lastIndex, index), isLink: false });
		}

		// Add the URL itself
		parts.push({ text: url, isLink: true });

		lastIndex = index + url.length;
	}

	// Add remaining text after the last URL
	if (lastIndex < value.length) {
		parts.push({ text: value.slice(lastIndex), isLink: false });
	}

	return (
		<span>
			{parts.map((part, i) =>
				part.isLink ? (
					<a key={i} href={part.text} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>
						{part.text}
					</a>
				) : (
					<React.Fragment key={i}>{part.text}</React.Fragment>
				)
			)}
		</span>
	);
};

// Renders the BE string "Order line {sku} was linked" (multiple entries pipe-joined)
// with the SKU styled as a "new value" badge and "linked" bolded.
const renderOrderLineLinkedSentence = newValue => {
	const entries = newValue.split(' | ');
	return entries.map((entry, i) => {
		const match = entry.match(/^Order line (.+) was linked$/);
		const body = match ? (
			<React.Fragment>
				Order line <span className="activity-log__badge activity-log__badge--new">{match[1]}</span> was{' '}
				<span className="type--wgt--bold">linked</span>
			</React.Fragment>
		) : (
			entry
		);
		return (
			<React.Fragment key={i}>
				{i > 0 && ' | '}
				{body}
			</React.Fragment>
		);
	});
};

// Map of HistoryLog `changed[].name` -> custom renderer for self-contained
// sentence-style records (BE writes the full sentence in `newValue` and leaves
// `initialValue` empty). Add an entry here for each new sentence-style record.
const sentenceLogRenderers = {
	'Order Line': renderOrderLineLinkedSentence,
};

const ListFieldLog = ({ name, index, action, value }) => {
	return (
		<li key={index} className="activity-log__list__item">
			{action} <span className="type--wgt--bold">{name}</span>{' '}
			<span className="activity-log__badge activity-log__badge--new">{value}</span>
		</li>
	);
};

export default observer(HistoryLogTemplate);
